'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { ProposalBackersOutcomesStack, type ProposalBackersOutcomesStackBacker } from '@/components/launchpad/proposal-backers-outcomes-stack';
import { ProposalProgressBar } from '@/components/launchpad/proposal-progress-bar';
import type { ProposalDisplayStatus } from '@/components/launchpad/proposal-status-badge';
import { AnimatedButton } from '@/components/ui/animated-button';
interface ProposalHeroStakeProps {
  proposal: {
    pubkey: string;
    totalFunded: string;
    creationThreshold: string | null;
    backerCount: number;
    status: ProposalDisplayStatus;
  };
  topBackers: ProposalBackersOutcomesStackBacker[];
  /** Mobile-only "Back proposal" CTA opens the sticky-bar sheet. */
  onMobileBackClick: () => void;
  /** The proposal went live and the page is handing off to its market. */
  launching?: boolean;
}

/**
 * Hero staking block — halftone progress bar with the amount/percentage
 * caption (Figma 3317:1388 bottom half), a hairline divider, then a footer
 * row showing up to five overlapping backer avatars on the left and "Backed by N
 * users" on the right. The mobile-only "Back proposal" CTA stays at the
 * bottom; on `lg+` the right column owns the action so the CTA is hidden.
 */
export function ProposalHeroStake(t0) {
  const $ = _c(35);
  const {
    proposal,
    topBackers,
    onMobileBackClick,
    launching: t1
  } = t0;
  const launching = t1 === undefined ? false : t1;
  const t = useTranslations("launchpad.detail");
  const tForm = useTranslations("launchpad.form");
  const locale = useLocale();
  let t2;
  if ($[0] !== proposal.creationThreshold || $[1] !== proposal.status || $[2] !== proposal.totalFunded) {
    t2 = <ProposalProgressBar totalFunded={proposal.totalFunded} threshold={proposal.creationThreshold} status={proposal.status} halftone={true} labelVariant="amount-and-percent" />;
    $[0] = proposal.creationThreshold;
    $[1] = proposal.status;
    $[2] = proposal.totalFunded;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="h-px w-full bg-border-primary" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== topBackers) {
    t4 = <ProposalBackersOutcomesStack topBackers={topBackers} />;
    $[5] = topBackers;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("backedByPrefix");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== locale || $[10] !== proposal.backerCount) {
    t6 = proposal.backerCount.toLocaleString(locale);
    $[9] = locale;
    $[10] = proposal.backerCount;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <span className="font-medium text-foreground-primary tabular-nums">{t6}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("backedBySuffix");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t7 || $[18] !== t8) {
    t9 = <span className="text-body text-foreground-tertiary">{t5}{" "}{t7}{" "}{t8}</span>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t4 || $[21] !== t9) {
    t10 = <div className="flex items-center justify-between gap-3">{t4}{t9}</div>;
    $[20] = t4;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== launching || $[24] !== t || $[25] !== tForm) {
    t11 = launching ? tForm("creatingMarket") : t("backCta");
    $[23] = launching;
    $[24] = t;
    $[25] = tForm;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== launching || $[28] !== onMobileBackClick || $[29] !== t11) {
    t12 = <AnimatedButton className="w-full @min-[1024px]/proposal-detail:hidden" onClick={onMobileBackClick} disabled={launching} loading={launching}>{t11}</AnimatedButton>;
    $[27] = launching;
    $[28] = onMobileBackClick;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t10 || $[32] !== t12 || $[33] !== t2) {
    t13 = <div className="flex flex-col gap-4">{t2}{t3}{t10}{t12}</div>;
    $[31] = t10;
    $[32] = t12;
    $[33] = t2;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}